import { buildProps, definePropType, hasValue } from '@ll-plus/utils'
import { has, isNull, isEqual } from 'lodash-es'

import type { ExtractPropTypes } from 'vue'
import type { FormProps as AntFormProps, TooltipProps } from 'ant-design-vue'
import type { FormColumn } from '@ll-plus/components'
import type advancedFiltering from './advanced-filtering.vue'
import type advancedFilteringParams from './components/advanced-filtering-params.vue'
import type advancedFilteringTags from './components/advanced-filtering-tags.vue'

export interface IAdvancedFilteringTag {
  label: string
  key: string
  value: string | string[]
  showValue: string | string[]
}

export const ALL_VALUE: string = '_ALL'

export type FilterColumn = FormColumn<{
  unUseDefaultOption?: boolean
  defaultOptionLabel?: string
}>

export const initValue = (columns: FilterColumn[], values: any) => {
  const copyValues = {}
  if (columns.length) {
    columns.forEach(column => {
      let value: any = has(values, column.key) ? values[column.key] : null
      if (has(column, 'options') && !column.unUseDefaultOption) {
        if (column.type === 'checkbox-group' || has(column, 'mode')) {
          value =
            !hasValue(value) || (Array.isArray(value) && value.length === 0)
              ? [ALL_VALUE]
              : value
        } else {
          value = !hasValue(value) ? ALL_VALUE : value
        }
      }
      copyValues[column.key] = value
    })
  }

  return copyValues
}

export const filterAllValue = (
  values: string | string[],
  column: FilterColumn
): any => {
  let value: any = null
  if (Array.isArray(values)) {
    value = values.filter(item => item !== ALL_VALUE || !hasValue(values))
    if (column && column.type === 'number-range') {
      value = values ? (values.every(e => !isNull(e)) ? values : null) : null
    }
  } else {
    value = values === ALL_VALUE || !hasValue(values) ? null : values
  }

  return value
}

export const treeDataFormat = (
  arr = [] as any[],
  fieldNames: { [key: string]: string }
) => {
  let options = [] as any[]
  if (!arr) return []
  options = arr.map((e: any) => {
    const obj: any = {}
    obj.label = e[fieldNames.label]
    obj.value = e[fieldNames.value]
    if (e[fieldNames.children] && e[fieldNames.children].length) {
      obj.children = treeDataFormat(e[fieldNames.children], fieldNames)
    }

    return obj
  })
  return options
}

// const isEqual = (val1,val2)=>{

// }

export const findLabelByValue = (
  tree: FilterColumn['options'],
  value: any
): string => {
  // 递归函数，用于遍历 Tree 数据
  function _findLabel(nodes: FilterColumn['options']): string[] {
    const labels: string[] = []
    if (!nodes) return []
    for (const node of nodes) {
      if (isEqual(node.value, value)) {
        labels.push(node.label)
      }
      if (node.children && node.children.length > 0) {
        const childLabels = _findLabel(node.children)
        if (childLabels) {
          labels.push(...childLabels)
        }
      }
    }
    return labels.length > 0 ? labels : []
  }
  // 开始递归查找
  return _findLabel(tree).join(',')
}

// export const hasValue = (value: any) => {
//   return Array.isArray(value)
//     ? value.length > 0
//     : !isNull(value) && !isUndefined(value) && value !== ''
// }

export const advancedFilteringProps = buildProps({
  filterColumns: {
    type: definePropType<FilterColumn[]>(Array),
    default: () => []
  },
  value: {
    type: Object,
    default: () => ({})
  },
  formOptions: {
    type: definePropType<AntFormProps>(Object),
    default: () => ({ layout: 'vertical' })
  },
  overlayStyle: {
    type: definePropType<TooltipProps['overlayStyle']>(Object),
    default: () => ({
      width: '500px'
    })
  }
} as const)

export const advancedFilteringParamsProps = buildProps({
  filterColumns: {
    type: definePropType<FilterColumn[]>(Array),
    default: () => []
  },
  value: {
    type: Object,
    default: () => ({})
  },
  formOptions: {
    type: definePropType<AntFormProps>(Object),
    default: () => ({ layout: 'vertical' })
  },
  showFooter: {
    type: Boolean,
    default: false
  }
} as const)

export const advancedFilteringEmits = {
  change: (value: Record<string, any>) => !!value,
  'update:value': (value: Record<string, any>) => !!value
}

export const advancedFilteringParamsEmits = {
  close: () => true,
  confirm: () => true,
  changeValue: (value: Record<string, any>) => !!value,
  'update:value': (value: Record<string, any>) => !!value
}

export const advancedFilteringTagsEmits = {
  change: (value: Record<string, any>) => !!value,
  'update:value': (value: Record<string, any>) => !!value
}

// props
export type AdvancedFilteringProps = ExtractPropTypes<
  typeof advancedFilteringProps
>

export type AdvancedFilteringParamsProps = ExtractPropTypes<
  typeof advancedFilteringParamsProps
>

// emits
export type AdvancedFilteringEmits = typeof advancedFilteringEmits

export type AdvancedFilteringParamsEmits = typeof advancedFilteringParamsEmits

export type AdvancedFilteringTagsEmits = typeof advancedFilteringTagsEmits

// instance
export type AdvancedFilteringInstance = InstanceType<typeof advancedFiltering>

export type AdvancedFilteringParamsInstance = InstanceType<
  typeof advancedFilteringParams
>

export type AdvancedFilteringTagsInstance = InstanceType<
  typeof advancedFilteringTags
>
